import { Box, BoxProps } from '@mui/material'
import { FC, Ref, useImperativeHandle, useRef } from 'react'
import { MouseInteractions, ReplayerEvents } from 'rrweb'
import rrwebPlayer from 'rrweb-player'
import 'rrweb-player/dist/style.css'
import './rrweb-player.css'
import flvCss from './plugin/players/flv.css?inline'
import { isErtDebugModeEnabled, SCRUBBER_UPDATE_ALWAYS_INTERVAL_MS, SCRUBBER_UPDATE_INTERVAL_MS } from './rrwebUtils'
import { useResizeObserver } from '../../../../hooks/useResizeObserver'
import { ProcessedRRWebEvents } from './types'
import { RRWebPlugin } from './plugin/RRWebPlugin'

type TimeChangedCallback = (currentTimeInSeconds: number) => void
type DurationCallback = (durationSeconds: number) => void
export type RRWebPlayerProxy = {
  onCurrentTimeChange: (handler: TimeChangedCallback) => void

  /**
   * Plays the recording.
   */
  play: () => void

  /**
   * Pauses the recording.
   */
  pause: () => void

  /**
   * Pauses the recording.
   */
  setSpeed: (speedMultiplier: number) => void

  /**
   * Seeks to a specific time in the recording, expressed in seconds.
   *
   * @remarks
   *
   * If the target falls outside the range of the recording, it will be clamped to the nearest valid value.
   */
  goto: (second: number) => void

  onDurationSeconds: (handler: DurationCallback) => void

  addEventListener: (event: string, handler: (params: unknown) => unknown) => void
}

/**
 * Props for the `UndecoratedReplayer` component.
 */
export type RRWebPlayerProps = BoxProps & {
  /**
   * The source RRWeb event sequence that makes up the recording.
   */
  processedRRWebEvents: ProcessedRRWebEvents

  rrwebPlugin: RRWebPlugin

  autoPlay?: boolean

  ref: Ref<RRWebPlayerProxy>
}

/**
 * Wrapper component for `rrweb` that provides an interface for controlling the playback of an RRWeb recording.
 */
export const RRWebPlayer: FC<RRWebPlayerProps> = ({ ref, processedRRWebEvents, rrwebPlugin, autoPlay = false, sx, ...boxProps }) => {
  const sizingContainerRef = useRef<HTMLDivElement>(undefined)
  const replayerContainerRef = useRef<HTMLDivElement>(undefined)
  const rrwebPlayerRef = useRef<rrwebPlayer>(undefined)
  const timeChangedCallbackRef = useRef<TimeChangedCallback>(null)
  const durationCallbackRef = useRef<DurationCallback>(null)
  const lastEmittedCurrentTimeRef = useRef<number>(0)

  // Previously suppressed `react-hooks/refs` here. Biome 1.9.4 has no equivalent rule, and this custom observer hook intentionally receives the current sizing element ref.
  useResizeObserver(sizingContainerRef.current, sizingContainer => {
    if (rrwebPlayerRef.current) {
      // @ts-expect-error - this is in the documentation but not in the types
      rrwebPlayerRef.current.$set({
        width: sizingContainer.offsetWidth,
        height: sizingContainer.offsetHeight
      })
      rrwebPlayerRef.current.triggerResize()
    }
  })

  useImperativeHandle(ref, () => {
    if (replayerContainerRef.current && !rrwebPlayerRef.current && processedRRWebEvents.events.length) {
      const flvStyleSheet = new CSSStyleSheet()
      flvStyleSheet.replaceSync?.(flvCss)

      const rect = replayerContainerRef.current.getBoundingClientRect()

      rrwebPlayerRef.current = new rrwebPlayer({
        target: replayerContainerRef.current,
        props: {
          events: processedRRWebEvents.events,
          maxScale: 2,
          width: rect.width,
          height: rect.height,
          plugins: [rrwebPlugin],
          UNSAFE_replayCanvas: true, // this removes the iframe sandbox which could be a security risk
          autoPlay,
          useVirtualDom: false, // this feature is buggy and the playback has issues when this is not disabled.
          showController: false,
          speedOption: [1],
          insertStyleRules: [...flvStyleSheet.cssRules, { cssText: '.rr-block {background: #000000}' }].map(rule => rule.cssText),
          showDebug: false,
          showWarning: false,
          skipInactive: false
        }
      })

      if (isErtDebugModeEnabled()) {
        rrwebPlayerRef.current.getReplayer().enableInteract()
      }

      rrwebPlayerRef.current.getReplayer().on(ReplayerEvents.MouseInteraction, e => {
        const { type } = e as { type: MouseInteractions }

        if (type === MouseInteractions.MouseDown || type === MouseInteractions.TouchStart) {
          const mouseEl = replayerContainerRef.current?.querySelector('.replayer-mouse')
          mouseEl?.classList.add('rr-mouse-down')
        } else if (type === MouseInteractions.MouseUp || type === MouseInteractions.TouchEnd || type === MouseInteractions.TouchCancel) {
          const mouseEl = replayerContainerRef.current?.querySelector('.replayer-mouse')
          mouseEl?.classList.remove('rr-mouse-down')
        }
      })

      rrwebPlayerRef.current?.addEventListener('ui-update-current-time', e => {
        const event = e as { payload: number }
        const timeInMillis = event.payload

        // this check is there to throttle the scrubber and controls updates to prevent a lot of unnecessary rendering
        if (
          timeChangedCallbackRef.current &&
          (Math.abs(timeInMillis - lastEmittedCurrentTimeRef.current) > SCRUBBER_UPDATE_INTERVAL_MS ||
            Math.abs((rrwebPlayerRef.current?.getMetaData?.().totalTime ?? 0) - timeInMillis) <= SCRUBBER_UPDATE_ALWAYS_INTERVAL_MS)
        ) {
          timeChangedCallbackRef.current(timeInMillis / 1000)
          lastEmittedCurrentTimeRef.current = timeInMillis
        }
      })
    }

    const rrwebPlayerProxy: RRWebPlayerProxy = {
      onCurrentTimeChange: (handler: TimeChangedCallback) => {
        timeChangedCallbackRef.current = handler
      },
      onDurationSeconds: (handler: DurationCallback) => {
        durationCallbackRef.current = handler

        if (rrwebPlayerRef.current) {
          handler(getDurationSeconds(rrwebPlayerRef.current))
        }
      },
      play: () => {
        rrwebPlayerRef.current?.play()
      },
      pause: () => {
        rrwebPlayerRef.current?.pause()
      },
      setSpeed(speedMultiplier: number): void {
        rrwebPlayerRef.current?.setSpeed(speedMultiplier)
      },
      goto: (millis: number) => {
        rrwebPlayerRef.current?.goto(millis) // this call internally just calls the play method from the time offset specified
      },
      addEventListener: (event: string, handler: (params: unknown) => unknown) => {
        rrwebPlayerRef.current?.addEventListener(event, handler)
      }
    }

    if (durationCallbackRef.current && rrwebPlayerRef.current) {
      durationCallbackRef.current(getDurationSeconds(rrwebPlayerRef.current))
    }

    return rrwebPlayerProxy
  }, [processedRRWebEvents, rrwebPlugin, autoPlay])

  return (
    <Box ref={sizingContainerRef} sx={{ ...{ position: 'relative' }, ...sx }} {...boxProps}>
      <Box ref={replayerContainerRef}>{processedRRWebEvents.events.length === 0 && "Can't play. Data missing."}</Box>
    </Box>
  )
}

function getDurationSeconds(player: rrwebPlayer) {
  return (player.getMetaData?.().totalTime ?? 0) / 1000
}
